import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import { Box, Card, CardContent, makeStyles, Paper, Theme, Typography } from '@material-ui/core';
import Popover from 'components/@common/Popover';
import { ReportEntityType } from 'components/ReportsModule/types';
import Skeleton from 'components/@common/Skeleton';
import MaybeRenderReportElement from '../../common/MaybeRenderReportElement';
import ModuleStatusChip from '../AccessmentAcceleratorComponents/ModuleStatusChip';
import { buildModuleData, calcMinutes } from '../keyboarding-report-util';
import { KeyAssessmentAndEnrichmentActivityAcceleratorModuleStats } from 'types/schema-generated-types';

type Props = {
  loading?: boolean;
  data?: KeyAssessmentAndEnrichmentActivityAcceleratorModuleStats[];
  entityType: ReportEntityType;
};

const AssessmentAcceleratorCard: React.FC<Props> = ({ loading, data, entityType }) => {
  const classes = useStyles();
  const { t } = useTranslation('Reports');
  const moduleData = buildModuleData(data);
  const isClassroomType = entityType === ReportEntityType.CLASSROOM;
  const containerRef = useRef<HTMLElement>();

  const hasData = (data: any) => {
    return data.secToComplete || data.questionsAnswered;
  };

  const getQuestionText = () =>
    isClassroomType
      ? t('Average No. of Questions Answered in One Try')
      : t('questions answered in one try');
  return (
    <Paper ref={containerRef} className={classes.cardContainer}>
      <Popover description={t('mostRecentData', { ns: 'ReportsHelpCommon' })}>
        <Typography component="h3" variant="body2">
          {t('Assessment Accelerator')}
        </Typography>
      </Popover>

      {loading ? (
        <Skeleton height={containerRef.current?.clientHeight!} />
      ) : (
        <MaybeRenderReportElement loading={loading!} reportData={moduleData}>
          {moduleData?.map((x, idx) => (
            <Card
              key={`assessment_card_${idx}`}
              variant="outlined"
              className={cx(classes.card, !hasData(x) && isClassroomType && classes.greyed)} // only grey out classroom level
            >
              <CardContent className={classes.cardContent}>
                <Box className={classes.flex}>
                  <Typography
                    className={classes.flexBetween}
                    variant="body2"
                    style={!hasData(x) ? { color: '#969696' } : {}}
                  >
                    {x.name} {t('mostRecent', { ns: 'ReportsHelpCommon' })}
                    {!isClassroomType && <ModuleStatusChip status={x.status} />}
                  </Typography>

                  {hasData(x) && (
                    <Box display="flex" justifyContent="space-between" style={{ flexFlow: 'wrap' }}>
                      <Box style={{ flexFlow: 'wrap' }}>
                        <Typography variant="h3" component="span">
                          {calcMinutes(x?.secToComplete!)}
                        </Typography>
                        <Typography
                          component="span"
                          className={classes.descriptorLabel}
                          style={{ flexBasis: 150 }}
                        >
                          {t('to complete')}
                        </Typography>
                      </Box>
                      <Box className={cx(classes.flex, isClassroomType && classes.flexEnd)}>
                        <Box>
                          <Typography variant="h3" component="span">
                            {x.questionsAnswered || 0}
                          </Typography>
                          <Typography component="span" className={classes.descriptorLabel}>
                            {getQuestionText()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </MaybeRenderReportElement>
      )}
    </Paper>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  flex: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  flexBetween: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  flexEnd: {
    justifyContent: 'flex-end !important'
  },
  card: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minHeight: 76
  },
  cardContent: {
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(1)
    }
  },
  cardContainer: {
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    height: '100%'
  },
  container: { marginBottom: theme.spacing(2) },
  primaryStatContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  descriptorLabel: {
    fontSize: 14,
    marginLeft: theme.spacing(0.5),
    color: theme.palette.grey[600]
  },

  greyed: {
    minHeight: 76,
    backgroundColor: '#EAEAEA'
  }
}));

export default AssessmentAcceleratorCard;
